import React, { useState } from "react";
import { Check, Plus, X, CaretDown, CaretUp } from "@phosphor-icons/react";
import {
  TREATMENT_OPTIONS, ASSISTIVE_DEVICE_OPTIONS, MISSED_OPTIONS,
  TAG_OPTIONS, WEATHER_OPTIONS, ACTIVITY_OPTIONS, HELPED_OPTIONS,
  VA_IMPACT_OPTIONS, DURATION_PRESETS, dedupePrescriptions, splitMedNameStrength,
  UNIFIED_TRIGGER_CHIPS, UNIFIED_TREATMENT_CHIPS, TREATMENT_HELPED_OPTIONS,
  prioritizeChips,
} from "@/lib/symptomDiary";
import {
  RELIEF_OPTIONS as CATALOG_RELIEFS,
} from "@/lib/symptomCatalog";

// ---- Shared chip selector (moved out of SymptomTrackerView) ----
export function ChipSelector({ options, value = [], onToggle, testId }) {
  return (
    <div className="appt-link-chips" data-testid={testId}>
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
            onClick={() => onToggle(opt)}
            data-testid={`${testId}-${opt.replace(/\s+/g, "-").toLowerCase()}`}
          >
            {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
            <span>{opt}</span>
          </button>
        );
      })}
    </div>
  );
}

function SectionCard({ title, hint, children, testId }) {
  return (
    <div className="sym-section" data-testid={testId}>
      <div className="sym-section-title">{title}</div>
      {hint && <div className="sym-section-hint">{hint}</div>}
      <div className="sym-section-body">{children}</div>
    </div>
  );
}
export { SectionCard };

/**
 * Inline expand/collapse "Advanced …" panel. Lives inside a SectionCard so the
 * v4 entry form can keep the casual chip-only view and tuck the detailed
 * fields (sliders, weather chips, per-med helped, etc.) behind one tap.
 */
export function AdvancedAccordion({ label = "Advanced details", children, testId, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="field field--span" data-testid={testId}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="btn btn--ghost"
        style={{
          padding: "6px 0",
          fontSize: 11,
          fontWeight: 700,
          color: "#1f3a6b",
          letterSpacing: "0.05em",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          background: "transparent",
          border: "none",
          cursor: "pointer",
        }}
        data-testid={`${testId || "advanced"}-toggle`}
      >
        {open ? <CaretUp size={11} weight="bold" /> : <CaretDown size={11} weight="bold" />}
        {label.toUpperCase()}
      </button>
      {open && (
        <div
          style={{
            marginTop: 8,
            paddingTop: 12,
            borderTop: "1px dashed rgba(60, 60, 67, 0.18)",
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
          data-testid={`${testId || "advanced"}-body`}
        >
          {children}
        </div>
      )}
    </div>
  );
}

function ToggleChip({ active, label, onToggle, testId }) {
  return (
    <button
      type="button"
      className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
      onClick={onToggle}
      data-testid={testId}
    >
      {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
      <span>{label}</span>
    </button>
  );
}

function MiniSlider({ label, value, onChange, testId }) {
  return (
    <div className="field">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <span className="field-label">{label}</span>
        <span style={{ fontWeight: 800, fontSize: 13, fontFamily: "JetBrains Mono, monospace", color: value != null ? "#1F3A6B" : "#94A3B8" }}>
          {value != null ? `${value}/10` : "not set"}
        </span>
      </div>
      <input
        type="range" min="0" max="10" step="1"
        value={value ?? 5}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testId}
        style={{ width: "100%" }}
      />
    </div>
  );
}

// ============================================================ DURATION PICKER
export function DurationPicker({ value, onChange, label = "DURATION" }) {
  const isPreset = DURATION_PRESETS.includes(value);
  return (
    <div className="field field--span">
      <span className="field-label">{label}</span>
      <div className="appt-link-chips" data-testid="symptom-duration-presets">
        {DURATION_PRESETS.map((opt) => {
          const active = value === opt;
          return (
            <button
              key={opt}
              type="button"
              className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
              onClick={() => onChange(active ? "" : opt)}
              data-testid={`symptom-duration-${opt.replace(/\s+/g, "-").toLowerCase()}`}
            >
              {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
              <span>{opt}</span>
            </button>
          );
        })}
      </div>
      <input
        className="field-input"
        style={{ marginTop: 8 }}
        value={isPreset ? "" : (value || "")}
        onChange={(e) => onChange(e.target.value)}
        placeholder={isPreset ? `Custom... (currently "${value}")` : 'Custom — e.g., "45 min", "3 days on and off"'}
        data-testid="symptom-duration"
      />
    </div>
  );
}

// ============================================================ VA EVIDENCE IMPACT
export function VaImpactSection({ form, toggleArr }) {
  // Old entries may carry catalog impact values that aren't in the VA list —
  // keep them selectable so editing never silently drops data.
  const options = Array.from(new Set([...VA_IMPACT_OPTIONS, ...(form.impact || [])]));
  return (
    <SectionCard
      title="WHAT DID IT PREVENT YOU FROM DOING?"
      hint="These map directly to the functional-impact language raters look for. Tap everything that applied."
      testId="va-impact-section"
    >
      <div className="field field--span">
        <ChipSelector
          options={options}
          value={form.impact || []}
          onToggle={(v) => toggleArr("impact", v)}
          testId="symptom-va-impact"
        />
      </div>
    </SectionCard>
  );
}

// ============================================================ FLARE DETAILS
export function FlareDetailSection({ form, patch, toggleArr }) {
  return (
    <SectionCard
      title="FLARE-UP DETAILS"
      hint="Document this episode the way an examiner will ask about it — when it ended, what it stopped you from doing, and what happens after repeated use."
      testId="flare-section"
    >
      <label className="field">
        <span className="field-label">FLARE END TIME (IF OVER)</span>
        <input
          type="datetime-local"
          className="field-input"
          value={form.endDate?.slice(0, 16) || ""}
          onChange={(e) => patch("endDate", e.target.value)}
          data-testid="symptom-end-date"
        />
      </label>
      <label className="field">
        <span className="field-label">DAYS MISSED (WORK / PT / DUTY)</span>
        <input
          type="number" min="0" step="0.5"
          className="field-input"
          value={form.missedDays ?? ""}
          onChange={(e) => patch("missedDays", e.target.value === "" ? null : Math.max(0, Number(e.target.value)))}
          placeholder="e.g., 1"
          data-testid="symptom-missed-days"
        />
      </label>
      <div className="field field--span">
        <span className="field-label">MISSED BECAUSE OF THIS FLARE</span>
        <ChipSelector
          options={MISSED_OPTIONS}
          value={form.missedActivities || []}
          onToggle={(v) => toggleArr("missedActivities", v)}
          testId="symptom-missed"
        />
      </div>
      <div className="field field--span">
        <span className="field-label">ASSISTIVE DEVICES USED</span>
        <ChipSelector
          options={ASSISTIVE_DEVICE_OPTIONS}
          value={form.assistiveDevices || []}
          onToggle={(v) => toggleArr("assistiveDevices", v)}
          testId="symptom-devices"
        />
      </div>
      <div className="field field--span" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        <ToggleChip
          active={!!form.worstDay}
          label="Mark as a worst-day example"
          onToggle={() => patch("worstDay", !form.worstDay)}
          testId="symptom-worstday"
        />
      </div>
      <label className="field field--span">
        <span className="field-label">REPEATED-USE IMPACT</span>
        <textarea
          className="field-input"
          rows={2}
          value={form.repeatedUseImpact || ""}
          onChange={(e) => patch("repeatedUseImpact", e.target.value)}
          placeholder='After repeated use (walking, lifting, a full shift) — e.g., "knee swells and I limp the rest of the day"'
          data-testid="symptom-repeated-use"
        />
      </label>
    </SectionCard>
  );
}

// ============================================================ FACTORS
export function FactorsSection({ form, patch }) {
  const factors = form.factors || {};
  const setFactor = (k, v) => patch("factors", { ...factors, [k]: v });
  const weather = factors.weather || [];

  return (
    <SectionCard
      title="FACTORS — WHAT MIGHT HAVE AFFECTED TODAY"
      hint="Optional. Patterns like 'bad sleep → worse pain' or 'cold weather → flare' show up in your insights and reports."
      testId="factors-section"
    >
      <MiniSlider
        label="SLEEP QUALITY LAST NIGHT"
        value={factors.sleepQuality ?? null}
        onChange={(v) => setFactor("sleepQuality", v)}
        testId="symptom-factor-sleep"
      />
      <MiniSlider
        label="STRESS LEVEL"
        value={factors.stressLevel ?? null}
        onChange={(v) => setFactor("stressLevel", v)}
        testId="symptom-factor-stress"
      />
      <div className="field field--span">
        <span className="field-label">WEATHER</span>
        <ChipSelector
          options={WEATHER_OPTIONS}
          value={weather}
          onToggle={(v) => setFactor("weather", weather.includes(v) ? weather.filter((x) => x !== v) : [...weather, v])}
          testId="symptom-factor-weather"
        />
      </div>
      <div className="field field--span">
        <span className="field-label">ACTIVITY LEVEL</span>
        <div className="appt-link-chips" data-testid="symptom-factor-activity">
          {ACTIVITY_OPTIONS.map((opt) => {
            const active = factors.activity === opt;
            return (
              <button
                key={opt}
                type="button"
                className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                onClick={() => setFactor("activity", active ? "" : opt)}
                data-testid={`symptom-factor-activity-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
      <label className="field">
        <span className="field-label">DIET / ALCOHOL</span>
        <input
          className="field-input"
          value={factors.diet || ""}
          onChange={(e) => setFactor("diet", e.target.value)}
          placeholder="e.g., red meat, beer (gout watch)"
          data-testid="symptom-factor-diet"
        />
      </label>
      <label className="field">
        <span className="field-label">WORK / DUTY SCHEDULE</span>
        <input
          className="field-input"
          value={factors.workSchedule || ""}
          onChange={(e) => setFactor("workSchedule", e.target.value)}
          placeholder='e.g., "night shift", "12-hr shifts this week"'
          data-testid="symptom-factor-schedule"
        />
      </label>
      <div className="field field--span" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <ToggleChip
          active={!!factors.missedMedication}
          label="Missed / skipped medication"
          onToggle={() => setFactor("missedMedication", !factors.missedMedication)}
          testId="symptom-factor-missedmed"
        />
      </div>
    </SectionCard>
  );
}

// ============================================================ MEDS + TREATMENT
export function MedsTreatmentSection({ form, patch, toggleArr, prescriptions = [], suggestedMeds = [], compact = false }) {
  const medsTaken = form.medsTaken || [];
  const rxChips = dedupePrescriptions(prescriptions);
  const takenBases = new Set(medsTaken.map((m) => splitMedNameStrength(m.name).base.toLowerCase()));

  function chipActive(chip) {
    return medsTaken.some((m) => chip.prescriptionIds.includes(m.prescriptionId))
      || takenBases.has(chip.base.toLowerCase());
  }
  function toggleRxChip(chip) {
    if (chipActive(chip)) {
      patch("medsTaken", medsTaken.filter((m) => !chip.prescriptionIds.includes(m.prescriptionId)
        && splitMedNameStrength(m.name).base.toLowerCase() !== chip.base.toLowerCase()));
    } else {
      patch("medsTaken", [...medsTaken, { prescriptionId: chip.prescriptionIds[0], name: chip.label, helped: "" }]);
    }
  }
  function setHelped(idx, v) {
    patch("medsTaken", medsTaken.map((m, i) => (i === idx ? { ...m, helped: v } : m)));
  }
  function removeMed(idx) {
    patch("medsTaken", medsTaken.filter((_, i) => i !== idx));
  }
  function addCustomMed(name) {
    const { base } = splitMedNameStrength(name);
    if (!base || takenBases.has(base.toLowerCase())) return;
    patch("medsTaken", [...medsTaken, { prescriptionId: "", name: splitMedNameStrength(name).strength ? `${base} ${splitMedNameStrength(name).strength}` : base, helped: "" }]);
  }

  // Condition-suggested meds (e.g. gout → Allopurinol / Indomethacin) that the
  // veteran doesn't already have as a prescription chip.
  const rxBases = new Set(rxChips.map((c) => c.base.toLowerCase()));
  const suggestions = suggestedMeds.filter((s) => !rxBases.has(s.toLowerCase()));

  return (
    <SectionCard
      title="MEDICATIONS & TREATMENT"
      hint="Connect this entry to what you actually took or did — and whether it helped. This builds your medication-effectiveness evidence."
      testId="meds-section"
    >
      {rxChips.length > 0 && (
        <div className="field field--span">
          <span className="field-label">FROM YOUR PRESCRIPTIONS (TAP IF TAKEN)</span>
          <div className="appt-link-chips" data-testid="symptom-rx-chips">
            {rxChips.map((chip) => {
              const active = chipActive(chip);
              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                  onClick={() => toggleRxChip(chip)}
                  data-testid={`symptom-med-${chip.prescriptionIds[0]}`}
                >
                  {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                  <span>{chip.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="field field--span">
          <span className="field-label">COMMON FOR THIS CONDITION</span>
          <div className="appt-link-chips" data-testid="symptom-suggested-meds">
            {suggestions.map((name) => {
              const active = takenBases.has(name.toLowerCase());
              return (
                <button
                  key={name}
                  type="button"
                  className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                  onClick={() => (active
                    ? patch("medsTaken", medsTaken.filter((m) => splitMedNameStrength(m.name).base.toLowerCase() !== name.toLowerCase()))
                    : addCustomMed(name))}
                  data-testid={`symptom-suggested-med-${name.toLowerCase()}`}
                >
                  {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                  <span>{name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <label className="field field--span">
        <span className="field-label">OTHER MEDICATION (PRESS ENTER TO ADD)</span>
        <input
          className="field-input"
          placeholder="e.g., Ibuprofen 800 mg, melatonin..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              e.preventDefault();
              addCustomMed(e.target.value);
              e.target.value = "";
            }
          }}
          data-testid="symptom-custom-med"
        />
      </label>
      {medsTaken.length > 0 && (
        <div className="field field--span" data-testid="symptom-meds-taken">
          {medsTaken.map((m, i) => (
            <div key={`${m.prescriptionId || m.name}-${i}`} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 700, flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {m.name}
              </span>
              <select
                className="field-input"
                style={{ width: 150, flex: "0 0 auto" }}
                value={m.helped || ""}
                onChange={(e) => setHelped(i, e.target.value)}
                data-testid={`symptom-med-helped-${i}`}
              >
                <option value="">Did it help?</option>
                {HELPED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
              <button type="button" className="icon-btn" onClick={() => removeMed(i)} data-testid={`symptom-med-remove-${i}`} title="Remove">
                <X size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      )}
      {!compact && (
        <div className="field field--span">
          <span className="field-label">TREATMENT USED</span>
          <ChipSelector
            options={TREATMENT_OPTIONS}
            value={form.treatments || []}
            onToggle={(v) => toggleArr("treatments", v)}
            testId="symptom-treatments"
          />
        </div>
      )}
    </SectionCard>
  );
}

// ============================================================ CONDITION TRACKER
function TrackerField({ field, value, onChange }) {
  const tid = `ct-${field.id}`;
  if (field.kind === "scale") {
    return <MiniSlider label={field.label.toUpperCase()} value={value ?? null} onChange={onChange} testId={tid} />;
  }
  if (field.kind === "count") {
    return (
      <label className="field">
        <span className="field-label">{field.label.toUpperCase()}</span>
        <input
          type="number" min="0"
          className="field-input"
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value === "" ? null : Math.max(0, Number(e.target.value)))}
          data-testid={tid}
        />
      </label>
    );
  }
  if (field.kind === "select") {
    return (
      <label className="field">
        <span className="field-label">{field.label.toUpperCase()}</span>
        <select className="field-input" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={tid}>
          <option value="">—</option>
          {field.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      </label>
    );
  }
  if (field.kind === "toggle") {
    return (
      <div className="field" style={{ display: "flex", alignItems: "flex-end" }}>
        <ToggleChip active={!!value} label={field.label} onToggle={() => onChange(!value)} testId={tid} />
      </div>
    );
  }
  if (field.kind === "chips") {
    const arr = value || [];
    return (
      <div className="field field--span">
        <span className="field-label">{field.label.toUpperCase()}</span>
        <ChipSelector
          options={field.options}
          value={arr}
          onToggle={(v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v])}
          testId={tid}
        />
      </div>
    );
  }
  return (
    <label className="field field--span">
      <span className="field-label">{field.label.toUpperCase()}</span>
      <input
        className="field-input"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ""}
        data-testid={tid}
      />
    </label>
  );
}

export function ConditionTrackerSection({ form, patch, tracker }) {
  if (!tracker) return null;
  const values = form.conditionFields || {};
  const setField = (k, v) => patch("conditionFields", { ...values, [k]: v });
  return (
    <SectionCard title={tracker.label.toUpperCase()} hint={tracker.hint} testId="condition-tracker-section">
      {tracker.fields.map((f) => (
        <TrackerField key={f.id} field={f} value={values[f.id]} onChange={(v) => setField(f.id, v)} />
      ))}
    </SectionCard>
  );
}

// ============================================================ TAGS
export function TagsSection({ form, patch, toggleArr }) {
  const tags = form.tags || [];
  return (
    <SectionCard
      title="TAGS"
      hint='Group logs for filtering and exports — e.g., tag your strongest entries "Evidence-ready" or "C&P prep".'
      testId="tags-section"
    >
      <div className="field field--span">
        <ChipSelector
          options={Array.from(new Set([...TAG_OPTIONS, ...tags]))}
          value={tags}
          onToggle={(v) => toggleArr("tags", v)}
          testId="symptom-tags"
        />
        <input
          className="field-input"
          style={{ marginTop: 8 }}
          placeholder="Add custom tag and press Enter..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              e.preventDefault();
              const v = e.target.value.trim();
              if (!tags.includes(v)) patch("tags", [...tags, v]);
              e.target.value = "";
            }
          }}
          data-testid="symptom-custom-tag"
        />
      </div>
    </SectionCard>
  );
}


// ============================================================ UNIFIED TRIGGERS (v4)
/**
 * Single chip cloud that replaces the old separate Triggers / Weather /
 * Activity / Diet / Work-schedule sections. Chips toggle `form.triggers[]`
 * directly so existing entries and insights keep working unchanged.
 *
 * Detailed factors (sleep / stress sliders, weather multi-select, diet text,
 * work schedule, missed-medication toggle) live behind an inline
 * "Advanced factors" accordion so casual logging stays one tap.
 */
export function UnifiedTriggersSection({ form, patch, toggleArr, conditionType }) {
  const factors = form.factors || {};
  const setFactor = (k, v) => patch("factors", { ...factors, [k]: v });
  const weather = factors.weather || [];
  const chips = prioritizeChips(UNIFIED_TRIGGER_CHIPS, conditionType, "triggers");
  // Always include any custom triggers the user already added so they remain visible.
  const allChips = Array.from(new Set([...chips, ...(form.triggers || [])]));
  return (
    <SectionCard
      title="WHAT CAUSED OR WORSENED IT?"
      hint="Tap anything that contributed today. Patterns (e.g., poor sleep → worse pain) bubble up into your insights."
      testId="unified-triggers-section"
    >
      <div className="field field--span">
        <ChipSelector
          options={allChips}
          value={form.triggers || []}
          onToggle={(v) => toggleArr("triggers", v)}
          testId="symptom-triggers"
        />
        <input
          className="field-input"
          style={{ marginTop: 8 }}
          placeholder="Add custom trigger and press Enter..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              e.preventDefault();
              const v = e.target.value.trim();
              if (!form.triggers.includes(v)) patch("triggers", [...form.triggers, v]);
              e.target.value = "";
            }
          }}
          data-testid="symptom-custom-trigger"
        />
      </div>
      <AdvancedAccordion label="Advanced factors" testId="triggers-advanced">
        <MiniSlider
          label="SLEEP QUALITY LAST NIGHT"
          value={factors.sleepQuality ?? null}
          onChange={(v) => setFactor("sleepQuality", v)}
          testId="symptom-factor-sleep"
        />
        <MiniSlider
          label="STRESS LEVEL"
          value={factors.stressLevel ?? null}
          onChange={(v) => setFactor("stressLevel", v)}
          testId="symptom-factor-stress"
        />
        <div className="field field--span">
          <span className="field-label">WEATHER</span>
          <ChipSelector
            options={WEATHER_OPTIONS}
            value={weather}
            onToggle={(v) => setFactor("weather", weather.includes(v) ? weather.filter((x) => x !== v) : [...weather, v])}
            testId="symptom-factor-weather"
          />
        </div>
        <div className="field field--span">
          <span className="field-label">ACTIVITY LEVEL</span>
          <div className="appt-link-chips" data-testid="symptom-factor-activity">
            {ACTIVITY_OPTIONS.map((opt) => {
              const active = factors.activity === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                  onClick={() => setFactor("activity", active ? "" : opt)}
                  data-testid={`symptom-factor-activity-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                  <span>{opt}</span>
                </button>
              );
            })}
          </div>
        </div>
        <label className="field">
          <span className="field-label">DIET / ALCOHOL DETAIL</span>
          <input
            className="field-input"
            value={factors.diet || ""}
            onChange={(e) => setFactor("diet", e.target.value)}
            placeholder="e.g., red meat, beer (gout watch)"
            data-testid="symptom-factor-diet"
          />
        </label>
        <label className="field">
          <span className="field-label">WORK / DUTY SCHEDULE</span>
          <input
            className="field-input"
            value={factors.workSchedule || ""}
            onChange={(e) => setFactor("workSchedule", e.target.value)}
            placeholder='e.g., "night shift", "12-hr shifts this week"'
            data-testid="symptom-factor-schedule"
          />
        </label>
        <div className="field field--span" style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <ToggleChip
            active={!!factors.missedMedication}
            label="Missed / skipped medication"
            onToggle={() => setFactor("missedMedication", !factors.missedMedication)}
            testId="symptom-factor-missedmed"
          />
        </div>
      </AdvancedAccordion>
    </SectionCard>
  );
}

// ============================================================ UNIFIED TREATMENT (v4)
/**
 * Combines the old Medication / Treatment / Relieved-by sections into one
 * "What did you do for it?" chip cloud, followed by a single overall
 * "Did it help?" selector. Granular controls (per-med helped, relief chips,
 * the wider treatment catalog) stay available behind "Advanced details".
 *
 * Selecting a non-Rx treatment chip writes into `form.treatments[]`; Rx and
 * custom medications still write into `form.medsTaken[]`. The overall helped
 * answer is stored in `form.treatmentHelped` and, when the user has not set a
 * per-med value, is mirrored onto each medsTaken entry so existing
 * medication-effectiveness insights keep working.
 */
export function UnifiedTreatmentSection({
  form, patch, toggleArr, prescriptions = [], suggestedMeds = [], conditionType,
}) {
  const medsTaken = form.medsTaken || [];
  const rxChips = dedupePrescriptions(prescriptions);
  const takenBases = new Set(medsTaken.map((m) => splitMedNameStrength(m.name).base.toLowerCase()));

  function rxChipActive(chip) {
    return medsTaken.some((m) => chip.prescriptionIds.includes(m.prescriptionId))
      || takenBases.has(chip.base.toLowerCase());
  }
  function toggleRxChip(chip) {
    if (rxChipActive(chip)) {
      patch("medsTaken", medsTaken.filter((m) => !chip.prescriptionIds.includes(m.prescriptionId)
        && splitMedNameStrength(m.name).base.toLowerCase() !== chip.base.toLowerCase()));
    } else {
      patch("medsTaken", [...medsTaken, { prescriptionId: chip.prescriptionIds[0], name: chip.label, helped: "" }]);
    }
  }
  function setHelped(idx, v) {
    patch("medsTaken", medsTaken.map((m, i) => (i === idx ? { ...m, helped: v } : m)));
  }
  function removeMed(idx) {
    patch("medsTaken", medsTaken.filter((_, i) => i !== idx));
  }
  function addCustomMed(name) {
    const { base, strength } = splitMedNameStrength(name);
    if (!base || takenBases.has(base.toLowerCase())) return;
    patch("medsTaken", [...medsTaken, { prescriptionId: "", name: strength ? `${base} ${strength}` : base, helped: "" }]);
  }
  // Map "Helped"/"Somewhat helped"/"Did not help"/"Not sure" to medsTaken helped vocab.
  function overallToPerMed(v) {
    if (v === "Helped") return "Helped";
    if (v === "Somewhat helped") return "Helped some";
    if (v === "Did not help") return "No relief";
    return "";
  }
  function setOverall(v) {
    patch("treatmentHelped", v);
    const mapped = overallToPerMed(v);
    if (mapped) {
      patch("medsTaken", (form.medsTaken || []).map((m) => (m.helped ? m : { ...m, helped: mapped })));
    }
  }

  const rxBases = new Set(rxChips.map((c) => c.base.toLowerCase()));
  const suggestions = suggestedMeds.filter((s) => !rxBases.has(s.toLowerCase()));
  const treatmentChips = prioritizeChips(UNIFIED_TREATMENT_CHIPS, conditionType, "treatments");

  return (
    <SectionCard
      title="WHAT DID YOU DO FOR IT?"
      hint="Tap the medications and anything else you used. Then tell us if it helped."
      testId="unified-treatment-section"
    >
      {rxChips.length > 0 && (
        <div className="field field--span">
          <span className="field-label">FROM YOUR PRESCRIPTIONS</span>
          <div className="appt-link-chips" data-testid="symptom-rx-chips">
            {rxChips.map((chip) => {
              const active = rxChipActive(chip);
              return (
                <button
                  key={chip.key}
                  type="button"
                  className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                  onClick={() => toggleRxChip(chip)}
                  data-testid={`symptom-med-${chip.prescriptionIds[0]}`}
                >
                  {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                  <span>{chip.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="field field--span">
          <span className="field-label">COMMON FOR THIS CONDITION</span>
          <div className="appt-link-chips" data-testid="symptom-suggested-meds">
            {suggestions.map((name) => {
              const active = takenBases.has(name.toLowerCase());
              return (
                <button
                  key={name}
                  type="button"
                  className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                  onClick={() => (active
                    ? patch("medsTaken", medsTaken.filter((m) => splitMedNameStrength(m.name).base.toLowerCase() !== name.toLowerCase()))
                    : addCustomMed(name))}
                  data-testid={`symptom-suggested-med-${name.toLowerCase()}`}
                >
                  {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                  <span>{name}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
      <div className="field field--span">
        <span className="field-label">TREATMENT / COPING USED</span>
        <ChipSelector
          options={treatmentChips}
          value={form.treatments || []}
          onToggle={(v) => toggleArr("treatments", v)}
          testId="symptom-treatments"
        />
      </div>
      <label className="field field--span">
        <span className="field-label">OTHER MEDICATION (PRESS ENTER TO ADD)</span>
        <input
          className="field-input"
          placeholder="e.g., Ibuprofen 800 mg, melatonin..."
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.value.trim()) {
              e.preventDefault();
              addCustomMed(e.target.value);
              e.target.value = "";
            }
          }}
          data-testid="symptom-custom-med"
        />
      </label>
      <div className="field field--span">
        <span className="field-label">DID IT HELP?</span>
        <div className="appt-link-chips" data-testid="symptom-treatment-helped">
          {TREATMENT_HELPED_OPTIONS.map((opt) => {
            const active = form.treatmentHelped === opt;
            return (
              <button
                key={opt}
                type="button"
                className={`appt-link-chip ${active ? "appt-link-chip--active" : ""}`}
                onClick={() => setOverall(active ? "" : opt)}
                data-testid={`symptom-treatment-helped-${opt.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {active ? <Check size={11} weight="bold" /> : <Plus size={11} weight="bold" />}
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
      </div>
      <AdvancedAccordion label="Advanced details" testId="treatment-advanced">
        {medsTaken.length > 0 && (
          <div className="field field--span" data-testid="symptom-meds-taken">
            <span className="field-label" style={{ marginBottom: 6, display: "block" }}>PER-MEDICATION RESULT</span>
            {medsTaken.map((m, i) => (
              <div key={`${m.prescriptionId || m.name}-${i}`} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={{ fontSize: 12, fontWeight: 700, flex: "1 1 auto", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.name}
                </span>
                <select
                  className="field-input"
                  style={{ width: 150, flex: "0 0 auto" }}
                  value={m.helped || ""}
                  onChange={(e) => setHelped(i, e.target.value)}
                  data-testid={`symptom-med-helped-${i}`}
                >
                  <option value="">Did it help?</option>
                  {HELPED_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <button type="button" className="icon-btn" onClick={() => removeMed(i)} data-testid={`symptom-med-remove-${i}`} title="Remove">
                  <X size={12} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
        <div className="field field--span">
          <span className="field-label">FULL TREATMENT CATALOG</span>
          <ChipSelector
            options={TREATMENT_OPTIONS}
            value={form.treatments || []}
            onToggle={(v) => toggleArr("treatments", v)}
            testId="symptom-treatments-full"
          />
        </div>
        <div className="field field--span">
          <span className="field-label">RELIEVED BY (SPECIFIC METHODS)</span>
          <ChipSelector
            options={CATALOG_RELIEFS}
            value={form.reliefMethods || []}
            onToggle={(v) => toggleArr("reliefMethods", v)}
            testId="symptom-reliefs"
          />
        </div>
      </AdvancedAccordion>
    </SectionCard>
  );
}
